import React, { useCallback } from 'react';
import { DateTime } from 'luxon';
import { FormattedMessage } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { FlexColumn, FlexRow } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import style from './ddp-introduction-modal.module.less';
import { DDP_MODAL_SNOOZE_KEY } from '../../../config/ddp-country-config';
import { useGlobalModalLaunchContext } from '../../../contexts/modal-context/global-modal-context';
import { useDdpCountryConfig } from '../../../hooks/use-ddp-country-config';
import { useLocalStorage } from '../../../hooks/use-local-storage';

/**
 * DDP Introduction Toast - displays country-specific messages
 * Only rendered when DDP is available for seller's country (via useDdpTermsState)
 * @returns {React.JSX.Element}
 */
export const DdpToastIntroduction = () => {
  const { openDdpTermsModal } = useGlobalModalLaunchContext();
  const [, setSnoozedUntilTimestamp] = useLocalStorage(DDP_MODAL_SNOOZE_KEY);

  // Get country-specific configuration and messages
  const { introductionMessages: messages, snoozeDurationHours } = useDdpCountryConfig();

  const handleRemindMeLater = useCallback(() => {
    const snoozeUntilTimestamp = `"${DateTime.now().plus({ hours: snoozeDurationHours }).toISO()}"`;
    setSnoozedUntilTimestamp(snoozeUntilTimestamp);
  }, [setSnoozedUntilTimestamp, snoozeDurationHours]);

  return (
    <FlexRow className={style['ddp-toast']}>
      <FlexRow className={style['toast-content']}>
        <FlexColumn className={style['graphic-section']}>
          <div className={style['ddp-graphic']} />
        </FlexColumn>

        <FlexColumn className={style['text-section']}>
          <Text className={style['modal-title']} textType={TextType.H2} value={messages.modalTitle} />
          <Text
            className={style['modal-description']}
            formattedMessageValues={{
              link: (chunks) => (
                <a
                  href='https://help.shipstation.com/hc/en-us/articles/4401816013595-Bill-International-Duties-to-Payor-of-Shipping-Charges'
                  rel='noopener noreferrer'
                  target='_blank'
                >
                  {chunks}
                </a>
              ),
            }}
            textType={TextType.Paragraph}
            value={messages.modalDescription}
          />

          <FlexColumn className={style['actions-section']}>
            <Button className={style['accept-button']} onClick={openDdpTermsModal} theme={THEME_CONSTANTS.primary}>
              <FormattedMessage {...messages.acceptTermsButton} />
            </Button>

            <Button className={style['remind-later-link']} onClick={handleRemindMeLater} theme={THEME_CONSTANTS.primary}>
              <FormattedMessage {...messages.remindMeLaterLink} />
            </Button>
          </FlexColumn>
        </FlexColumn>
      </FlexRow>
    </FlexRow>
  );
};
